import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
    searchUsersByFullNameOrLocker,
    type Referente,
} from "../../firebase/firestore/usuarios";

interface Option {
    id: string;
    name: string;
    lockerCode?: string;
}

/**
 * Autocomplete que busca usuarios existentes (por nombre/apellido/casillero)
 * y devuelve una referencia ligera {id, name}. Se usa para asignar
 * "Vendedor" y "Referido por" a un usuario.
 */
export default function UserAutocomplete({
    label,
    value,
    onChange,
    excludeId,
}: {
    label: string;
    value?: Referente;
    onChange: (ref?: Referente) => void;
    excludeId?: string;
}) {
    const [input, setInput] = useState("");
    const [options, setOptions] = useState<Option[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const term = input.trim();
        if (term.length < 2) {
            setOptions([]);
            return;
        }

        let active = true;
        setLoading(true);
        const handler = setTimeout(async () => {
            try {
                const users = await searchUsersByFullNameOrLocker(term);
                if (!active) return;
                const opts = users
                    .filter((u) => u.id !== excludeId)
                    .map((u) => ({
                        id: u.id,
                        name: `${u.name ?? ""} ${u.lastName ?? ""}`.trim(),
                        lockerCode: u.lockerCode,
                    }));
                setOptions(opts);
            } catch (error) {
                console.error("Error buscando usuarios:", error);
            } finally {
                if (active) setLoading(false);
            }
        }, 350);

        return () => {
            active = false;
            clearTimeout(handler);
        };
    }, [input, excludeId]);

    const selected: Option | null = value
        ? { id: value.id, name: value.name }
        : null;

    // Incluir el valor seleccionado en las opciones para evitar warnings de MUI.
    const mergedOptions = useMemo(() => {
        const sel: Option | null = value
            ? { id: value.id, name: value.name }
            : null;
        if (sel && !options.some((o) => o.id === sel.id)) {
            return [sel, ...options];
        }
        return options;
    }, [options, value]);

    return (
        <Autocomplete
            size="small"
            options={mergedOptions}
            loading={loading}
            value={selected}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            getOptionLabel={(o) =>
                o.lockerCode ? `${o.name} (${o.lockerCode})` : o.name
            }
            onChange={(_, newValue) =>
                onChange(
                    newValue ? { id: newValue.id, name: newValue.name } : undefined
                )
            }
            onInputChange={(_, newInput) => setInput(newInput)}
            filterOptions={(x) => x}
            noOptionsText={
                input.trim().length < 2 ? "Escribe para buscar..." : "Sin resultados"
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
